import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import lodashFilter from 'lodash/filter';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashPick from 'lodash/pick';

import constants from '../Config/constants';
import { pluralize, smartJoin } from '../Helper';
import DateHelper from '../Helper/Date';
import StoreHelper from '../Helper/Store';
import { checkoutOrderTypeSelector } from '../RTK/checkout/selectors';
import { addMealPlanItem } from '../RTK/mealPlan';
import {
  selectIsCheckingOut,
  selectIsDayHasCart,
  selectMealPlanCounter,
  selectMealPlanDay,
  selectMealPlanType,
} from '../RTK/mealPlan/selectors';
import { shopSelector } from '../RTK/shop/selectors';

import useAlert from './useAlert';

const { ORDER_TYPES } = constants;

function useMealPlan() {
  const alert = useAlert();
  const dispatch = useDispatch();
  const selectedDay = useSelector(selectMealPlanDay);
  const selectedType = useSelector(selectMealPlanType);
  const shopData = useSelector(shopSelector);
  const orderType = useSelector((state) =>
    checkoutOrderTypeSelector(state, shopData?.id, true),
  );
  const isCheckingOut = useSelector(selectIsCheckingOut);
  const isSelectedDayHasCart = useSelector((state) =>
    selectIsDayHasCart(state, [selectedDay?.value]),
  );
  const cartCounter = useSelector((state) =>
    selectMealPlanCounter(state, selectedDay?.value),
  );

  const orderTypes = lodashFilter(
    constants.ORDER_TYPE_DATA,
    (d) => lodashPick(shopData, constants.ORDER_TYPE_API_KEYS)[d.apiDataKey],
  );
  const isMealPlan = useMemo(() => {
    const orderTypeUrlParam = constants.isWeb
      ? new URLSearchParams(document?.location?.search).get('order_type')
      : '';
    const isValidOrderType =
      (!lodashIsEmpty(orderType) && shopData?.[orderType?.apiDataKey]) || // has order type AND its enabled on store settings
      Boolean(orderTypeUrlParam); // OR has url parameter for order type (on meal plan only store, is_meal_plan_only is false because is_delivery_active is also false)
    if (isValidOrderType) {
      return orderType?.value === ORDER_TYPES.MEAL_PLAN;
    } else {
      const availableOrderTypes =
        StoreHelper.getStoreAvailableOrderTypes(shopData);
      const defaultOrderType = availableOrderTypes[0]?.value; // default order type of the store
      return defaultOrderType === ORDER_TYPES.MEAL_PLAN;
    }
  }, [orderType, shopData]);
  const isMealPlanOnly = isMealPlan && orderTypes.length <= 1;
  const isMealPlanAndHasType = isMealPlan && Boolean(selectedType);
  const dayCount = Number(selectedType?.substring?.(0, 1) || 0);
  const dispatchTime = useMemo(() => {
    if (!shopData?.meal_plan_order_time) return '';
    const orderTimeInitial = DateHelper.getDayjsAtTime(
      shopData?.meal_plan_order_time,
    );
    return `${orderTimeInitial.format(
      constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
    )} to ${orderTimeInitial
      .add(constants.minuteInterval, 'minute')
      .format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT)}`;
  }, [shopData?.meal_plan_order_time]);

  const _validate = useCallback(
    (noAlert) => {
      if (selectedDay.value === 1) {
        if (!noAlert && !isSelectedDayHasCart) {
          alert(
            'Meal for Monday',
            'Please add your meal plan for monday to proceed for next day',
          );
        }
        return isSelectedDayHasCart;
      } else {
        const isMatchOnMonday = cartCounter.current === cartCounter.monday;
        if (!noAlert && !isMatchOnMonday) {
          const more = cartCounter.monday - cartCounter.current;
          alert(
            'Item Count Must Match Monday',
            `You need to add ${more} more ${pluralize(
              more,
              'item',
            )} to proceed to the next day.`,
          );
        }
        return isMatchOnMonday;
      }
    },
    [cartCounter, isSelectedDayHasCart, selectedDay.value, alert],
  );

  const _onAdd = (item) => {
    if (
      item.is_exclusive &&
      !constants.MEAL_PLAN_EXCLUSIVE_AVAILABILITY.includes(selectedDay.value)
    ) {
      const exclusiveAvailability =
        constants.MEAL_PLAN_EXCLUSIVE_AVAILABILITY.map(
          DateHelper.convertNumberToDay,
        );
      alert(
        `You Can Add This Starting ${exclusiveAvailability[0]}`,
        `Exclusive items are available on ${smartJoin(
          exclusiveAvailability,
        )}. You're almost there — keep planning your days!`,
      );
    } else {
      const nextCartCount = cartCounter.current + item.quantity;
      if (selectedDay.value !== 1) {
        if (nextCartCount > cartCounter.monday) {
          const pluralizeItemWord = pluralize(cartCounter.monday, 'item');
          return alert(
            "Match Monday's Item Count",
            `You can only add ${cartCounter.monday} ${pluralizeItemWord} to your plan to match Monday's total.`,
          );
        }
      } else if (nextCartCount > constants.MEAL_PLAN_MAX_ITEM_PER_DAY) {
        // add meal plan item on monday (max 10 items, quantity incldued)
        const pluralizeItemWord = pluralize(
          constants.MEAL_PLAN_MAX_ITEM_PER_DAY,
          'item',
        );
        return alert(
          'Maximum Reached',
          `You can only add up to ${constants.MEAL_PLAN_MAX_ITEM_PER_DAY} ${pluralizeItemWord} in your Monday plan.`,
        );
      }
      dispatch(addMealPlanItem({ day: selectedDay.value, item }));
    }
  };

  return {
    isMealPlan,
    isMealPlanAndHasType,
    isMealPlanCheckingOut: isCheckingOut,
    isMealPlanOnly,
    isSelectedDayHasCart,
    mealPlanCartCounter: cartCounter,
    mealPlanDay: selectedDay,
    mealPlanDayCount: dayCount,
    mealPlanDiscount: shopData?.meal_plan_discount,
    mealPlanDispatchTime: dispatchTime,
    mealPlanType: selectedType,
    shopData,
    onAddMealPlan: _onAdd,
    validateMealPlan: _validate,
  };
}

export default useMealPlan;
